import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { BIDDING } from 'modules/fleet/events';

import * as http from 'utils/http';
import config from 'config';

const BiddingContext = createContext();

const fetcher = (url) => http.get(`${config.API_URL_V2}${url}`);

function BiddingContextProvider({ children, eventEmitter, events }) {
  const [isOpened, setIsOpened] = useState(false);
  const [journeyId, setJourneyId] = useState(null);
  const [isTriggeringAction, setIsTriggeringAction] = useState(false);
  const [isBidAccepted, setIsBidAccepted] = useState(false);
  const [error, setError] = useState(false);

  const reset = useCallback(() => {
    setIsOpened(false);
    setJourneyId(null);
    setIsTriggeringAction(false);
    setIsBidAccepted(false);
    setError(false);
  }, []);

  const close = useCallback(() => {
    setIsOpened(false);
    setJourneyId(null);
  }, []);

  const handleOpen = useCallback((params) => {
    setIsOpened(true);
    setJourneyId(params?.journeyId);
  }, []);

  const callEventAction = useCallback(
    (bid) => {
      BIDDING.CALL_DRIVER({
        journey_id: journeyId,
        cost: bid?.cost,
        scoring: bid?.scoring,
        driver_id: bid?.driver_id,
        driver_name: bid?.driver_name,
      });
    },
    [journeyId]
  );

  const assignAction = useCallback(
    async (bid) => {
      setIsTriggeringAction(true);
      try {
        await http.put(`${config.API_URL}mercury/journeys/${journeyId}/assign/`, {
          driver_id: bid?.driver_id,
          vehicle: null,
          vehicle_ids: [],
        });
        await http.patch(`${config.API_URL}mercury/journeys/${journeyId}/`, {
          bid_cost_for_journey: bid?.cost,
        });
        await http.post(`${config.API_URL}mercury/journeys/${journeyId}/logs/`, {
          action: 'ASSIGN_DRIVER',
          reason: 'REASON_OTHER',
          comment: '',
          changes: {
            carrier_name_after: bid?.carrier_name,
            driver_name_after: bid?.driver_name,
          },
        });
        setIsBidAccepted({
          driverAssigned: bid?.driver_name,
          bidCost: bid?.cost,
          bidCurrency: bid?.currency,
        });
      } catch (err) {
        const errorFound = http.getError(err);
        setError(errorFound);
      } finally {
        setIsTriggeringAction(false);
      }
    },
    [journeyId]
  );

  const { data: bidsData } = useSWR(
    journeyId ? `assignment/bidding/journey/${journeyId}/` : null,
    fetcher,
    {}
  );

  const maxCostChanges = bidsData?.max_cost_changes;
  const listOfBids = bidsData?.bids;
  const listOfRejectedBids = bidsData?.rejected_bids;
  const bidsIds = useMemo(
    () =>
      Array.from(listOfBids || [])
        .concat(Array.from(listOfRejectedBids || []))
        .map((bid) => bid.id),
    [listOfBids, listOfRejectedBids]
  );

  useEffect(() => {
    eventEmitter.on(events.OPEN_BIDDING, handleOpen);
    return () => {
      eventEmitter.off(events.OPEN_BIDDING);
    };
  });

  useEffect(() => {
    if (!isOpened) {
      reset();
    }
  }, [isOpened, reset]);

  useEffect(() => {
    const markBidsAsViewed = async () => {
      if (!bidsIds?.length) {
        return;
      }
      try {
        await http.post(`${config.API_URL}assignment/bidding/mark-as-viewed/`, {
          ids: bidsIds,
        });
        eventEmitter.emit(events.BIDDING_VIEWED);
      } catch (err) {
        console.error(err);
      }
    };

    markBidsAsViewed();
  }, [bidsIds, eventEmitter, events.BIDDING_VIEWED]);

  return (
    <BiddingContext.Provider
      value={{
        close,
        isOpened,
        listOfBids,
        listOfRejectedBids,
        maxCostChanges,
        journeyId,
        callEventAction,
        assignAction,
        isTriggeringAction,
        isBidAccepted,
        error,
      }}
    >
      {children}
    </BiddingContext.Provider>
  );
}

export { BiddingContextProvider };
export default BiddingContext;
